import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { CopilotButton } from './CopilotButton';
import { CopilotPanel } from './CopilotPanel';
import { useCopilotContext } from './useCopilotContext';

const CopilotProviderContext = createContext(null);

/**
 * Copilot Provider - wraps authenticated pages to provide Copilot functionality
 * Now includes conversation history persistence
 */
export const CopilotProvider = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [prefilledPrompt, setPrefilledPrompt] = useState(null);
  const [conversationId, setConversationId] = useState(null);
  const [conversationHistory, setConversationHistory] = useState([]);
  const { context, setEntityContext, clearEntityContext, setFilters, getFullContext } = useCopilotContext();

  // Debug log
  useEffect(() => {
    console.log('CopilotProvider open state:', open);
  }, [open]);

  const openCopilot = useCallback((prompt = null, extraContext = {}) => {
    console.log('openCopilot called with prompt:', prompt);
    if (prompt) {
      setPrefilledPrompt({ prompt, context: extraContext });
    }
    setOpen(true);
  }, []);

  const closeCopilot = useCallback(() => {
    setOpen(false);
    setPrefilledPrompt(null);
  }, []);

  const handleButtonClick = useCallback(() => {
    console.log('Copilot button clicked, current open:', open);
    setOpen(true);
  }, [open]);

  // Start a new conversation
  const startNewConversation = useCallback(() => {
    setConversationId(null);
    setConversationHistory([]);
  }, []);

  // Load an existing conversation
  const loadConversation = useCallback((id, messages) => {
    setConversationId(id);
    setConversationHistory(messages || []);
  }, []);

  // Update conversation after sending a message
  const updateConversation = useCallback((newConversationId, userMessage, assistantMessage) => {
    setConversationId(newConversationId);
    setConversationHistory(prev => [
      ...prev,
      { role: 'user', content: userMessage },
      { role: 'assistant', content: assistantMessage }
    ]);
  }, []);

  const value = {
    open,
    openCopilot,
    closeCopilot,
    context,
    setEntityContext,
    clearEntityContext,
    setFilters,
    prefilledPrompt,
    clearPrefilledPrompt: () => setPrefilledPrompt(null),
    // Conversation persistence
    conversationId,
    conversationHistory,
    startNewConversation,
    loadConversation,
    updateConversation,
  };

  return (
    <CopilotProviderContext.Provider value={value}>
      {children}
      {ReactDOM.createPortal(
        <>
          <CopilotButton onClick={handleButtonClick} />
          <CopilotPanel
            open={open}
            onOpenChange={setOpen}
            context={context}
            setEntityContext={setEntityContext}
          />
        </>,
        document.body
      )}
    </CopilotProviderContext.Provider>
  );
};

/**
 * Hook to access Copilot functionality from any component
 */
export const useCopilot = () => {
  const context = useContext(CopilotProviderContext);
  if (!context) {
    throw new Error('useCopilot must be used within a CopilotProvider');
  }
  return context;
};

export default CopilotProvider;
